import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { useStore } from "ra-core";

/**
 * Kolonnebredder som kan dras, og som huskes per bruker og per liste.
 *
 * Bredden lagres på kolonnens `source`, ikke på indeksen, slik at den
 * overlever at kolonner skjules eller flyttes. Nøkkelen følger samme
 * konvensjon som DataTable bruker for kolonnevalg og rekkefølge
 * (`${storeKey}_columnRanks`), og går via ra-cores store → localStorage.
 *
 * Dramekanikken er den samme som i useJusterbarBredde:
 * lyttere på window, ikke på håndtaket, så draget overlever at musa forlater
 * elementet — og lagring skjer på pointerup, ikke på hver pointermove, så vi
 * ikke skriver til localStorage hundre ganger i sekundet.
 */

export const MIN_COLUMN_WIDTH = 60;

export type ColumnWidths = Record<string, number>;

/**
 * Lar «Tilbakestill»-knappen i kolonnevelgeren nullstille bredder sammen med
 * rekkefølge og skjulte kolonner. Bor her, ikke i data-table.tsx, fordi
 * columns-button.tsx ellers ville importert data-table.tsx i ring.
 */
export const ColumnWidthResetContext = createContext<
  { reset: () => void; hasCustomWidths: boolean } | undefined
>(undefined);

export const useColumnWidths = (storeKey: string) => {
  const [stored, setStored] = useStore<ColumnWidths>(
    `${storeKey}_columnWidths`,
    {},
  );

  // Bredder mens draget pågår. null = ingen drag, bruk de lagrede.
  const [live, setLive] = useState<ColumnWidths | null>(null);

  // Ref-ene holder callbackene stabile, så window-lytterne registreres én gang.
  const storedRef = useRef(stored);
  storedRef.current = stored;
  const liveRef = useRef<ColumnWidths | null>(null);
  const drag = useRef<{
    key: string;
    startX: number;
    startWidth: number;
    base: ColumnWidths;
  } | null>(null);

  const onPointerMove = useCallback((event: PointerEvent) => {
    const current = drag.current;
    if (!current) return;
    const width = Math.max(
      MIN_COLUMN_WIDTH,
      Math.round(current.startWidth + (event.clientX - current.startX)),
    );
    const next = { ...current.base, [current.key]: width };
    liveRef.current = next;
    setLive(next);
  }, []);

  const stopDrag = useCallback(() => {
    if (!drag.current) return;
    drag.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    const next = liveRef.current;
    liveRef.current = null;
    setLive(null);
    if (next) setStored(next);
  }, [setStored]);

  useEffect(() => {
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", stopDrag);
    window.addEventListener("pointercancel", stopDrag);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", stopDrag);
      window.removeEventListener("pointercancel", stopDrag);
    };
  }, [onPointerMove, stopDrag]);

  /**
   * pointerdown-håndterer for draghåndtaket. Startbredden måles på selve
   * hodecellen, så et drag virker også på en kolonne som ennå ikke har en
   * lagret bredde.
   *
   * stopPropagation er ikke valgfritt: i DataTable ligger håndtaket inne i
   * sorteringsknappen sin celle, og uten dette ville hvert drag også sortert
   * tabellen.
   */
  const startResize = useCallback(
    (key: string) => (event: React.PointerEvent<HTMLElement>) => {
      const cell = event.currentTarget.closest<HTMLElement>(
        "th, [data-column-header]",
      );
      if (!cell) return;
      event.preventDefault();
      event.stopPropagation();
      drag.current = {
        key,
        startX: event.clientX,
        startWidth: cell.getBoundingClientRect().width,
        base: storedRef.current,
      };
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },
    [],
  );

  const reset = useCallback(() => setStored({}), [setStored]);

  return {
    widths: live ?? stored,
    hasCustomWidths: Object.keys(stored).length > 0,
    startResize,
    reset,
  };
};
